import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DsChipColorVariant, DsSegmentedButtonOption } from '@levelaccess/design-system';

import { TranslateService } from '@client/app/translate/translate.service';
import { auditIssuesViewStatus } from '../../../../shared/constants/audit-issues-view';
import { FeatureFlagCollection } from '../../../../shared/interfaces/feature-flag.interface';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

enum FormField {
  status = 'status',
}

type RuleFilterForm = FormGroup<{
  [FormField.status]: FormControl<auditIssuesViewStatus>;
}>;

@Component({
  selector: 'app-rule-filter',
  templateUrl: './rule-filter.component.html',
  styleUrls: ['./rule-filter.component.scss'],
  standalone: false,
})
export class RuleFilterComponent implements OnChanges, OnDestroy {
  @Input() public status: auditIssuesViewStatus;

  @Input() public failedRulesCount: number;
  @Input() public reviewRulesCount: number;
  @Input() public passedRulesCount: number;
  @Input() public hidePassedRules: boolean;

  @Output() public onFilterChanged: EventEmitter<auditIssuesViewStatus> = new EventEmitter<auditIssuesViewStatus>();

  public readonly issueStatus: typeof auditIssuesViewStatus = auditIssuesViewStatus;
  public readonly FormField: typeof FormField = FormField;

  public formGroup: RuleFilterForm;
  public options: DsSegmentedButtonOption[] = [];

  private subscription: Subscription = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.formGroup = this.formBuilder.group({
      [FormField.status]: this.formBuilder.control<auditIssuesViewStatus>(auditIssuesViewStatus.failed, { nonNullable: true }),
    }) as RuleFilterForm;

    this.buildOptions();
    this.watchChanges();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const control: FormControl<auditIssuesViewStatus> | null = this.formGroup?.get(
      FormField.status,
    ) as FormControl<auditIssuesViewStatus> | null;

    if (changes['status'] && control && this.status !== null && control.value !== this.status) {
      control.setValue(this.status, { emitEvent: false });
    }

    if (changes['hidePassedRules'] && this.hidePassedRules === true && control?.value === auditIssuesViewStatus.passed) {
      control.setValue(auditIssuesViewStatus.failed, { emitEvent: true });
    }

    if (changes['failedRulesCount'] || changes['reviewRulesCount'] || changes['passedRulesCount'] || changes['hidePassedRules']) {
      this.buildOptions();
    }
  }

  public onLegacyRadioChange(value: auditIssuesViewStatus): void {
    this.formGroup.get(FormField.status)?.setValue(value);
  }

  public get useDsRuleFilter(): boolean {
    return this.featureFlagService.variation(FeatureFlagCollection.ruleFilterDesignSystem, true) as boolean;
  }

  private watchChanges(): void {
    const control: FormControl<auditIssuesViewStatus> | null = this.formGroup.get(
      FormField.status,
    ) as FormControl<auditIssuesViewStatus> | null;
    if (!control) return;

    this.subscription.add(
      control.valueChanges.subscribe((value: auditIssuesViewStatus) => {
        this.onFilterChanged.emit(value);
      }),
    );
  }

  private buildOptions(): void {
    const opts: DsSegmentedButtonOption[] = [
      {
        value: auditIssuesViewStatus.failed,
        label: this.translateService.instant('issues_filter_status_failed'),
        chip: {
          text: String(this.failedRulesCount ?? 0),
          colorVariant: DsChipColorVariant.Danger,
        },
      },
      {
        value: auditIssuesViewStatus.review,
        label: this.translateService.instant('issues_filter_status_flagged_for_review'),
        chip: {
          text: String(this.reviewRulesCount ?? 0),
          colorVariant: DsChipColorVariant.Info,
        },
      },
    ];

    if (!this.hidePassedRules) {
      opts.push({
        value: auditIssuesViewStatus.passed,
        label: this.translateService.instant('issues_filter_status_passed'),
        chip: {
          text: String(this.passedRulesCount ?? 0),
          colorVariant: DsChipColorVariant.Success,
        },
      });
    }

    this.options = opts;
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
